/**
 * BASKETS & THINGS — craft-builder.js
 * Interactive Snack Builder with HTML5 Canvas animations
 */

(function () {
  'use strict';

  // ── Product Pricing Data ──────────────────────────────────────
  const ITEMS = {
    strawberry: {
      title:   'Craft Your Strawberries 🍓',
      sizes:   [
        { label: 'Half Dozen', price: 30 },
        { label: 'Dozen',      price: 55 },
        { label: '2 Dozen',    price: 100 },
      ],
      hasExtras: false,
      draw: drawStrawberry,
    },
    pretzel: {
      title:   'Craft Your Pretzel Rods 🥨',
      sizes:   [
        { label: 'Dozen',   price: 20 },
        { label: '2 Dozen', price: 35 },
      ],
      hasExtras: false,
      draw: drawPretzel,
    },
    rkt: {
      title:   'Craft Your Rice Krispie Treats ⭐',
      sizes:   [
        { label: 'Half Dozen', price: 25 },
        { label: 'Dozen',      price: 45 },
        { label: '2 Dozen',    price: 80 },
      ],
      hasExtras: false,
      draw: drawRKT,
    },
    oreo: {
      title:   'Craft Your Chocolate Oreos 🍪',
      sizes:   [
        { label: 'Half Dozen', price: 45 },
        { label: 'Dozen',      price: 80 },
      ],
      hasExtras: false,
      draw: drawOreo,
    },
  };

  const THEMES = {
    'Classic':       { base: '#3D1A00', name: 'Dark Chocolate' },
    'White Gold':    { base: '#F5E6C8', name: 'White Chocolate' },
    'Milk Choc':     { base: '#7B3F00', name: 'Milk Chocolate' },
    '💜 Purple':    { base: '#7B1FA2', name: 'Purple' },
    '🌸 Pink':      { base: '#E91E8C', name: 'Pink' },
    '🎄 Christmas': { base: '#1B5E20', name: 'Christmas Green' },
    '🕷 Spider-Man':{ base: '#C62828', name: 'Spider-Man Red' },
    '🐣 Easter':    { base: '#CE93D8', name: 'Easter Pastel' },
    '🎂 Birthday':  { base: '#FF6BB5', name: 'Birthday Pink' },
    '👑 Luxury':    { base: '#1A1A1A', name: 'Black Luxury' },
  };

  const TOPPINGS = [
    { id: 'gold',     icon: '✨', label: 'Gold Flakes' },
    { id: 'gems',     icon: '💎', label: 'Gems' },
    { id: 'sparkles', icon: '🌟', label: 'Sparkles' },
    { id: 'sprinkles',icon: '🎊', label: 'Sprinkles' },
    { id: 'pearls',   icon: '⚪', label: 'Pearls' },
    { id: 'coconut',  icon: '🌴', label: 'Coconut' },
  ];

  const DRIZZLE_COLORS = [
    { c: '#D4AF37', n: 'Gold'   },
    { c: '#FFFFFF', n: 'White'  },
    { c: '#E91E8C', n: 'Pink'   },
    { c: '#7B1FA2', n: 'Purple' },
    { c: '#1565C0', n: 'Blue'   },
    { c: '#C62828', n: 'Red'    },
    { c: '#1B5E20', n: 'Green'  },
    { c: '#000000', n: 'Black'  },
  ];

  // ── State ─────────────────────────────────────────────────────
  let currentItem = null;
  let state = {};
  let cartItems = [];

  function resetState(itemId) {
    const item = ITEMS[itemId];
    state = {
      itemId,
      theme:         'Classic',
      chocBase:      THEMES['Classic'].base,
      chocName:      THEMES['Classic'].name,
      dipped:        false,
      toppings:      [],
      drizzleColor:  '#D4AF37',
      drizzled:      false,
      size:          item.sizes[0],
      qty:           1,
      extras:        [],
      uploadedImg:   null,
    };
  }

  // ── DOM helpers ───────────────────────────────────────────────
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function el(tag, cls, html) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html !== undefined) e.innerHTML = html;
    return e;
  }

  // ── Open / Close builder ──────────────────────────────────────
  function openBuilder(itemId) {
    if (!ITEMS[itemId]) return;
    currentItem = itemId;
    resetState(itemId);
    renderBuilderModal();
    const overlay = qs('#bt-builder-overlay');
    if (overlay) { overlay.classList.add('is-open'); }
    requestAnimationFrame(() => drawItem());
  }

  function closeBuilder() {
    const overlay = qs('#bt-builder-overlay');
    if (overlay) overlay.classList.remove('is-open');
    currentItem = null;
  }

  window.btOpenBuilder = openBuilder;
  window.btCloseBuilder = closeBuilder;

  // ── Modal render ──────────────────────────────────────────────
  function renderBuilderModal() {
    const item = ITEMS[currentItem];

    // Header
    const titleEl = qs('#bt-builder-title');
    if (titleEl) titleEl.textContent = item.title;

    // Body
    const body = qs('#bt-builder-body');
    if (!body) return;

    body.innerHTML = '';

    // ─ Left: canvas col ─
    const canvasCol = el('div', 'builder-canvas-col');
    const canvas = el('canvas');
    canvas.id  = 'bt-craft-canvas';
    canvas.width  = 232;
    canvas.height = 232;
    canvasCol.appendChild(canvas);

    const priceWrap = el('div', 'builder-price-display');
    priceWrap.innerHTML = `
      <span class="builder-price-label">Your Total</span>
      <span class="builder-price" id="bt-builder-price">$${item.sizes[0].price}.00</span>
    `;
    canvasCol.appendChild(priceWrap);

    const qtyCtrl = el('div', 'qty-control');
    qtyCtrl.innerHTML = `
      <button class="qty-btn" id="bt-qty-minus" aria-label="Decrease">−</button>
      <span class="qty-value" id="bt-qty-value">1</span>
      <button class="qty-btn" id="bt-qty-plus" aria-label="Increase">+</button>
    `;
    canvasCol.appendChild(qtyCtrl);

    const tagsEl = el('div', 'summary-tags');
    tagsEl.id = 'bt-summary-tags';
    canvasCol.appendChild(tagsEl);

    const addBtn = el('button', 'builder-add-btn');
    addBtn.id = 'bt-add-btn';
    addBtn.innerHTML = '🛒 Add to Sweet Bag';
    canvasCol.appendChild(addBtn);

    body.appendChild(canvasCol);

    // ─ Right: options col ─
    const optCol = el('div', 'builder-options-col');
    optCol.id = 'bt-options-col';
    body.appendChild(optCol);

    renderOptions();
    bindBuilderEvents();
  }

  function renderOptions() {
    const col = qs('#bt-options-col');
    if (!col) return;
    const item = ITEMS[currentItem];
    let stepNum = 1;

    function makeStep(title, content) {
      const step = el('div', 'build-step');
      step.innerHTML = `
        <div class="build-step__title">
          <span class="build-step__num">${stepNum++}</span>${title}
        </div>
      `;
      step.appendChild(content);
      return step;
    }

    col.innerHTML = '';

    // Step 1: Size
    const sizeRow = el('div', 'size-row');
    item.sizes.forEach((s, i) => {
      const btn = el('button', `size-chip${s.label === state.size.label ? ' is-active' : ''}`);
      btn.innerHTML = `${s.label}<span class="size-chip__price">$${s.price}</span>`;
      btn.addEventListener('click', () => {
        state.size = s;
        qsa('.size-chip').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        updatePrice();
      });
      sizeRow.appendChild(btn);
    });
    col.appendChild(makeStep('Choose Size', sizeRow));

    // Step 2: Theme
    const themeRow = el('div', 'theme-row');
    Object.keys(THEMES).forEach(name => {
      const btn = el('button', `theme-chip${state.theme === name ? ' is-active' : ''}`);
      btn.textContent = name;
      btn.addEventListener('click', () => {
        state.theme    = name;
        state.chocBase = THEMES[name].base;
        state.chocName = THEMES[name].name;
        state.dipped   = true;
        qsa('.theme-chip').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const dipBtn = qs('#bt-dip-btn');
        if (dipBtn) dipBtn.classList.add('is-dipped');
        animateDip();
        updateSummaryTags();
      });
      themeRow.appendChild(btn);
    });
    col.appendChild(makeStep('Choose Theme & Color', themeRow));

    // Step 3: Dip
    const dipWrap = el('div');
    const dipBtn  = el('button', `dip-btn${state.dipped ? ' is-dipped' : ''}`);
    dipBtn.id = 'bt-dip-btn';
    dipBtn.innerHTML = state.dipped ? '✅ Dipped! Tap to Re-Dip' : '🍫 Dip in Chocolate!';
    dipBtn.addEventListener('click', () => {
      state.dipped = true;
      dipBtn.classList.add('is-dipped');
      dipBtn.innerHTML = '✅ Dipped! Tap to Re-Dip';
      animateDip();
      updateSummaryTags();
    });
    dipWrap.appendChild(dipBtn);
    col.appendChild(makeStep('Dip in Chocolate', dipWrap));

    // Step 4: Toppings
    const tGrid = el('div', 'toppings-grid');
    TOPPINGS.forEach(t => {
      const btn = el('button', `topping-chip${state.toppings.includes(t.id) ? ' is-active' : ''}`);
      btn.innerHTML = `<span class="topping-chip__icon">${t.icon}</span>${t.label}`;
      btn.addEventListener('click', () => {
        const idx = state.toppings.indexOf(t.id);
        if (idx >= 0) state.toppings.splice(idx, 1);
        else state.toppings.push(t.id);
        btn.classList.toggle('is-active');
        drawItem();
        updateSummaryTags();
      });
      tGrid.appendChild(btn);
    });
    col.appendChild(makeStep('Add Toppings', tGrid));

    // Step 5: Extras (not for pretzel/oreo)
    if (item.hasExtras) {
      const extRow = el('div', 'theme-row');
      [
        { id: 'strawberries', label: '🍓 Real Strawberries +$2' },
        { id: 'chessman',     label: '🍪 Chessman Cookies +$2'  },
      ].forEach(x => {
        const btn = el('button', `theme-chip${state.extras.includes(x.id) ? ' is-active' : ''}`);
        btn.textContent = x.label;
        btn.addEventListener('click', () => {
          const idx = state.extras.indexOf(x.id);
          if (idx >= 0) state.extras.splice(idx, 1);
          else state.extras.push(x.id);
          btn.classList.toggle('is-active');
          updatePrice();
          updateSummaryTags();
        });
        extRow.appendChild(btn);
      });
      col.appendChild(makeStep('Add-Ons (+$2 each)', extRow));
    }

    // Step N: Drizzle
    const dRow = el('div', 'drizzle-row');
    DRIZZLE_COLORS.forEach(d => {
      const sw = el('div', `drizzle-swatch${state.drizzleColor === d.c ? ' is-active' : ''}`);
      sw.style.background = d.c;
      sw.style.border = '1px solid #ccc';
      sw.title = d.n;
      sw.addEventListener('click', () => {
        state.drizzleColor = d.c;
        qsa('.drizzle-swatch').forEach(s => s.classList.remove('is-active'));
        sw.classList.add('is-active');
      });
      dRow.appendChild(sw);
    });
    const dApply = el('button', 'drizzle-apply-btn', '✍️ Drizzle!');
    dApply.addEventListener('click', () => {
      state.drizzled = true;
      animateDrizzle();
      updateSummaryTags();
    });
    dRow.appendChild(dApply);
    col.appendChild(makeStep('Add Drizzle', dRow));

    // Upload
    const uploadWrap = el('div', 'build-step');
    uploadWrap.innerHTML = `
      <div class="build-step__title">
        <span class="build-step__num">${stepNum}</span>Inspiration Photo (optional)
      </div>
    `;
    const uploadArea = el('div', 'upload-area');
    uploadArea.innerHTML = `
      <input type="file" id="bt-upload-input" accept="image/*">
      <div class="upload-area__icon">📷</div>
      <p class="upload-area__text">Upload an inspiration photo or color swatch<br><small>Helps us match your vision perfectly!</small></p>
    `;
    uploadArea.addEventListener('click', () => qs('#bt-upload-input').click());
    uploadWrap.appendChild(uploadArea);
    col.appendChild(uploadWrap);
  }

  function bindBuilderEvents() {
    // Qty
    const minus = qs('#bt-qty-minus');
    const plus  = qs('#bt-qty-plus');
    if (minus) minus.addEventListener('click', () => { state.qty = Math.max(1, state.qty - 1); qs('#bt-qty-value').textContent = state.qty; updatePrice(); });
    if (plus)  plus.addEventListener('click',  () => { state.qty += 1; qs('#bt-qty-value').textContent = state.qty; updatePrice(); });

    // Add to cart
    const addBtn = qs('#bt-add-btn');
    if (addBtn) addBtn.addEventListener('click', addToCart);

    // Upload
    const uploadInput = qs('#bt-upload-input');
    if (uploadInput) {
      uploadInput.addEventListener('change', e => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => { state.uploadedImg = ev.target.result; };
        reader.readAsDataURL(file);
      });
    }
  }

  // ── Price ─────────────────────────────────────────────────────
  function updatePrice() {
    const extras = state.extras.length * 2;
    const total  = (state.size.price + extras) * state.qty;
    const el = qs('#bt-builder-price');
    if (el) el.textContent = `$${total.toFixed(2)}`;
  }

  function updateSummaryTags() {
    const tags = [
      state.dipped ? state.chocName : null,
      state.theme !== 'Classic' ? state.theme : null,
      ...state.toppings.map(t => ({ gold: '✨ Gold Flakes', gems: '💎 Gems', sparkles: '🌟 Sparkles', sprinkles: '🎊 Sprinkles', pearls: '⚪ Pearls', coconut: '🌴 Coconut' }[t] || t)),
      state.drizzled ? '✍️ Drizzle' : null,
      ...state.extras.map(e => e === 'strawberries' ? '🍓 +Strawberries' : '🍪 +Chessman'),
    ].filter(Boolean);

    const tagsEl = qs('#bt-summary-tags');
    if (tagsEl) {
      tagsEl.innerHTML = tags.map(t => `<span class="summary-tag">${t}</span>`).join('');
    }
  }

  // ── Cart ──────────────────────────────────────────────────────
  function addToCart() {
    const extras = state.extras.length * 2;
    const price  = (state.size.price + extras) * state.qty;
    const customs = [
      state.dipped   ? state.chocName : 'Not dipped',
      state.theme !== 'Classic' ? `Theme: ${state.theme}` : null,
      state.toppings.length ? `Toppings: ${state.toppings.join(', ')}` : null,
      state.drizzled ? `Drizzle: ${state.drizzleColor}` : null,
      ...state.extras.map(e => e === 'strawberries' ? '+Real Strawberries (+$2)' : '+Chessman Cookies (+$2)'),
    ].filter(Boolean);

    cartItems.push({
      id:      Date.now(),
      itemId:  state.itemId,
      name:    ITEMS[state.itemId].title.replace(/[🍓🥨⭐🍪]/u, '').trim().split('\n')[0],
      size:    state.size.label,
      price,
      qty:     state.qty,
      customs,
    });

    const btn = qs('#bt-add-btn');
    if (btn) {
      btn.classList.add('is-added');
      btn.innerHTML = '✅ Added to Sweet Bag!';
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.innerHTML = '🛒 Add to Sweet Bag';
      }, 1600);
    }

    updateCartUI();
    showToast(`🎉 ${ITEMS[state.itemId].title.split(' ').slice(0, 3).join(' ')} added!`);
    setTimeout(() => closeBuilder(), 1700);

    // If we're in Shopify, also post to the cart via AJAX
    if (window.Shopify) {
      // Shopify cart AJAX — line item properties carry the customizations
      // The actual product variant ID would come from the product page;
      // for the builder we store all choices as properties so the merchant sees them.
    }
  }

  function updateCartUI() {
    const count    = cartItems.reduce((s, i) => s + i.qty, 0);
    const subtotal = cartItems.reduce((s, i) => s + i.price, 0);

    // Update counter badges
    qsa('.bt-cart-count').forEach(el => { el.textContent = count; });

    // Update subtotal
    const subtEl = qs('#bt-cart-subtotal');
    if (subtEl) subtEl.textContent = `$${subtotal.toFixed(2)}`;

    // Render items
    const body = qs('#bt-cart-body');
    if (!body) return;

    if (cartItems.length === 0) {
      body.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty__icon">🍫</div>
          <p>Your bag is empty. Craft something sweet!</p>
        </div>`;
      return;
    }

    body.innerHTML = cartItems.map(item => `
      <div class="cart-item" data-id="${item.id}">
        <div class="cart-item__img" style="background:linear-gradient(135deg,#4A1D8C,#E91E8C);display:flex;align-items:center;justify-content:center;color:white;font-size:1.6rem;border-radius:14px;width:72px;height:72px;flex-shrink:0;">
          🍫
        </div>
        <div class="cart-item__info">
          <div class="cart-item__name">${item.name}</div>
          <div class="cart-item__meta">${item.size} · ${item.customs.slice(0, 2).join(' · ')}</div>
          <div class="cart-item__price">$${item.price.toFixed(2)} ×${item.qty}</div>
        </div>
        <button class="cart-item__remove" data-remove="${item.id}" aria-label="Remove">✕</button>
      </div>
    `).join('');

    // Remove listeners
    qsa('[data-remove]', body).forEach(btn => {
      btn.addEventListener('click', e => {
        const id = parseInt(e.currentTarget.dataset.remove, 10);
        cartItems = cartItems.filter(i => i.id !== id);
        updateCartUI();
      });
    });
  }

  // ── Toast ─────────────────────────────────────────────────────
  function showToast(msg) {
    let toast = qs('#bt-toast');
    if (!toast) {
      toast = el('div', 'bt-toast');
      toast.id = 'bt-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  }
  window.btShowToast = showToast;

  // ── Canvas Drawing ────────────────────────────────────────────
  function getCanvas()  { return qs('#bt-craft-canvas'); }
  function getCtx()     { const c = getCanvas(); return c ? c.getContext('2d') : null; }
  function drawItem()   { if (currentItem && ITEMS[currentItem]) ITEMS[currentItem].draw(); }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  function drawToppings(ctx, cx, cy, rw, rh, shape) {
    const pts = [];
    for (let i = 0; i < 24; i++) {
      let x, y;
      if (shape === 'circle') {
        const a  = Math.random() * Math.PI * 2;
        const r2 = Math.random() * (rw - 10);
        x = cx + Math.cos(a) * r2;
        y = cy + Math.sin(a) * r2;
      } else if (shape === 'ellipse') {
        const a = Math.random() * Math.PI * 2;
        x = cx + Math.cos(a) * rw * (0.3 + Math.random() * 0.6);
        y = cy + Math.sin(a) * rh * (0.3 + Math.random() * 0.6);
      } else {
        x = cx - rw + Math.random() * rw * 2;
        y = cy - rh + Math.random() * rh * 2;
      }
      pts.push({ x, y });
    }

    if (state.toppings.includes('gold')) {
      pts.slice(0, 8).forEach(p => {
        ctx.save(); ctx.fillStyle = '#D4AF37'; ctx.globalAlpha = 0.88;
        ctx.beginPath();
        for (let j = 0; j < 5; j++) {
          const a = (j / 5) * Math.PI * 2 - Math.PI / 2;
          const r = j % 2 === 0 ? 4 : 2;
          j === 0 ? ctx.moveTo(p.x + Math.cos(a) * r, p.y + Math.sin(a) * r)
                  : ctx.lineTo(p.x + Math.cos(a) * r, p.y + Math.sin(a) * r);
        }
        ctx.closePath(); ctx.fill(); ctx.restore();
      });
    }

    if (state.toppings.includes('gems')) {
      const gc = ['#E91E8C','#7B1FA2','#1565C0','#00897B','#D4AF37','#FF5722'];
      pts.slice(0, 7).forEach((p, i) => {
        ctx.save();
        ctx.fillStyle = gc[i % gc.length];
        ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 0.5;
        ctx.shadowColor = gc[i % gc.length]; ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - 6); ctx.lineTo(p.x + 6, p.y);
        ctx.lineTo(p.x, p.y + 4); ctx.lineTo(p.x - 6, p.y);
        ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.restore();
      });
    }

    if (state.toppings.includes('sparkles')) {
      pts.slice(0, 8).forEach(p => {
        ctx.save(); ctx.fillStyle = '#FFD700'; ctx.globalAlpha = 0.9;
        ctx.font = '10px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('✦', p.x, p.y); ctx.restore();
      });
    }

    if (state.toppings.includes('sprinkles')) {
      const sc = ['#E91E8C','#7B1FA2','#1565C0','#2E7D32','#D4AF37','#FF5722'];
      pts.forEach((p, i) => {
        ctx.save(); ctx.fillStyle = sc[i % sc.length]; ctx.globalAlpha = 0.9;
        ctx.translate(p.x, p.y); ctx.rotate(Math.random() * Math.PI);
        roundRect(ctx, -4, -1.5, 8, 3, 1.5); ctx.fill(); ctx.restore();
      });
    }

    if (state.toppings.includes('pearls')) {
      pts.slice(0, 6).forEach(p => {
        ctx.save();
        const g = ctx.createRadialGradient(p.x - 2, p.y - 2, 1, p.x, p.y, 5);
        g.addColorStop(0, '#fff'); g.addColorStop(1, '#DDD');
        ctx.fillStyle = g;
        ctx.shadowColor = 'rgba(0,0,0,0.25)'; ctx.shadowBlur = 3;
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      });
    }

    if (state.toppings.includes('coconut')) {
      pts.slice(0, 10).forEach(p => {
        ctx.save(); ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.translate(p.x, p.y); ctx.rotate(Math.random() * Math.PI);
        ctx.fillRect(-5, -1, 10, 2); ctx.restore();
      });
    }
  }

  function drawDrizzle(ctx, x1, x2, y1, y2) {
    ctx.save();
    ctx.strokeStyle = state.drizzleColor;
    ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.shadowColor = state.drizzleColor; ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.moveTo(x1 + 10, y1 + 20);
    ctx.lineTo(x2 - 10, y1 + 20);
    ctx.lineTo(x1 + 10, (y1 + y2) / 2);
    ctx.lineTo(x2 - 10, (y1 + y2) / 2);
    ctx.lineTo(x1 + 10, y2 - 20);
    ctx.lineTo(x2 - 10, y2 - 20);
    ctx.stroke();
    ctx.restore();
  }

  // ─ Individual item drawers ─
  function drawStrawberry() {
    const ctx = getCtx(); if (!ctx) return;
    ctx.clearRect(0, 0, 232, 232);
    const cx = 116, cy = 130;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.28)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 10;
    ctx.beginPath(); ctx.ellipse(cx, cy, 52, 68, 0, 0, Math.PI * 2);
    ctx.fillStyle = state.dipped ? state.chocBase : '#CC2929';
    ctx.fill(); ctx.restore();

    if (state.dipped) {
      const gr = ctx.createRadialGradient(cx - 18, cy - 28, 5, cx, cy, 65);
      gr.addColorStop(0, 'rgba(255,255,255,0.45)');
      gr.addColorStop(0.5, 'rgba(255,255,255,0.1)');
      gr.addColorStop(1, 'rgba(0,0,0,0.2)');
      ctx.beginPath(); ctx.ellipse(cx, cy, 52, 68, 0, 0, Math.PI * 2);
      ctx.fillStyle = gr; ctx.fill();

      for (let i = 0; i < 5; i++) {
        const dx = cx - 28 + i * 14;
        ctx.beginPath();
        ctx.moveTo(dx, cy + 55);
        ctx.bezierCurveTo(dx + 2, cy + 68, dx - 2, cy + 70, dx, cy + 72);
        ctx.strokeStyle = state.chocBase; ctx.lineWidth = 4 + Math.random() * 2;
        ctx.lineCap = 'round'; ctx.stroke();
      }
    } else {
      ctx.fillStyle = '#FFD700';
      for (let r = -1; r <= 1; r++) for (let c = -2; c <= 2; c++) {
        ctx.beginPath(); ctx.ellipse(cx + r * 17, cy + c * 19 - 10, 3, 5, r * 0.3, 0, Math.PI * 2); ctx.fill();
      }
    }

    // Stem
    ctx.fillStyle = '#2E7D32';
    ctx.beginPath(); ctx.moveTo(cx, cy - 68);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
      ctx.lineTo(cx + Math.cos(a) * 22, cy - 80 + Math.sin(a) * 8);
      ctx.lineTo(cx + Math.cos(a) * 12, cy - 70);
    }
    ctx.closePath(); ctx.fill();

    drawToppings(ctx, cx, cy, 52, 68, 'ellipse');
    if (state.drizzled) drawDrizzle(ctx, cx - 52, cx + 52, cy - 60, cy + 60);
  }

  function drawPretzel() {
    const ctx = getCtx(); if (!ctx) return;
    ctx.clearRect(0, 0, 232, 232);
    const cx = 116, baseY = 36;
    const rodW = 30, rodH = 178;
    const handleH = Math.floor(rodH * 0.27);
    const chocY   = baseY + handleH;
    const chocH   = rodH - handleH;

    // Handle (uncoated)
    ctx.fillStyle = '#C8A96E';
    roundRect(ctx, cx - rodW / 2, baseY, rodW, handleH, 8); ctx.fill();
    ctx.fillStyle = 'rgba(90,50,0,0.18)';
    for (let i = 0; i < 4; i++) { ctx.fillRect(cx - rodW / 2 + 4, baseY + i * (handleH / 4) + 4, rodW - 8, 2); }

    // Chocolate body (3/4)
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.25)'; ctx.shadowBlur = 12; ctx.shadowOffsetX = 3;
    ctx.fillStyle = state.dipped ? state.chocBase : '#C8A96E';
    roundRect(ctx, cx - rodW / 2, chocY, rodW, chocH, 8); ctx.fill(); ctx.restore();

    if (state.dipped) {
      const gr = ctx.createLinearGradient(cx - rodW / 2, chocY, cx + rodW / 2, chocY);
      gr.addColorStop(0, 'rgba(255,255,255,0.32)');
      gr.addColorStop(0.4, 'rgba(255,255,255,0.05)');
      gr.addColorStop(1, 'rgba(0,0,0,0.14)');
      roundRect(ctx, cx - rodW / 2, chocY, rodW, chocH, 8);
      ctx.fillStyle = gr; ctx.fill();

      // Drip at junction
      for (let i = 0; i < 3; i++) {
        const dx = cx - 8 + i * 8;
        ctx.beginPath(); ctx.moveTo(dx, chocY);
        ctx.bezierCurveTo(dx - 2, chocY - 10, dx + 2, chocY - 14, dx, chocY - 16);
        ctx.strokeStyle = state.chocBase; ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.stroke();
      }
    }

    // Tip
    ctx.fillStyle = state.dipped ? state.chocBase : '#C8A96E';
    ctx.beginPath(); ctx.arc(cx, baseY + rodH, rodW / 2, 0, Math.PI); ctx.fill();

    drawToppings(ctx, cx, chocY + chocH / 2, rodW, chocH / 2, 'rect');
    if (state.drizzled) drawDrizzle(ctx, cx - rodW / 2, cx + rodW / 2, chocY, baseY + rodH);
  }

  function drawRKT() {
    const ctx = getCtx(); if (!ctx) return;
    ctx.clearRect(0, 0, 232, 232);
    const cx = 116, cy = 116, w = 140, h = 110;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.22)'; ctx.shadowBlur = 16; ctx.shadowOffsetY = 8;
    roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 16);
    ctx.fillStyle = state.dipped ? state.chocBase : '#F5DEB3';
    ctx.fill(); ctx.restore();

    if (!state.dipped) {
      ctx.fillStyle = 'rgba(190,150,70,0.6)';
      for (let r = 0; r < 8; r++) for (let c = 0; c < 6; c++) {
        if (Math.random() > 0.35) {
          roundRect(ctx, cx - w / 2 + 12 + c * 20, cy - h / 2 + 12 + r * 12, 8, 6, 2);
          ctx.fill();
        }
      }
    } else {
      const gr = ctx.createLinearGradient(cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2);
      gr.addColorStop(0, 'rgba(255,255,255,0.38)');
      gr.addColorStop(0.45, 'rgba(255,255,255,0.07)');
      gr.addColorStop(1, 'rgba(0,0,0,0.14)');
      roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 16);
      ctx.fillStyle = gr; ctx.fill();

      for (let i = 0; i < 4; i++) {
        const dx = cx - 40 + i * 28;
        const dh = 8 + Math.random() * 12;
        ctx.beginPath();
        ctx.moveTo(dx, cy + h / 2);
        ctx.quadraticCurveTo(dx + 4, cy + h / 2 + dh, dx, cy + h / 2 + dh);
        ctx.strokeStyle = state.chocBase; ctx.lineWidth = 7; ctx.lineCap = 'round'; ctx.stroke();
      }
    }

    drawToppings(ctx, cx, cy, w / 2, h / 2, 'rect');
    if (state.drizzled) drawDrizzle(ctx, cx - w / 2, cx + w / 2, cy - h / 2, cy + h / 2);
  }

  function drawOreo() {
    const ctx = getCtx(); if (!ctx) return;
    ctx.clearRect(0, 0, 232, 232);
    const cx = 116, cy = 116, r = 82;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.32)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 8;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = state.dipped ? state.chocBase : '#1A0000';
    ctx.fill(); ctx.restore();

    if (!state.dipped) {
      ctx.strokeStyle = 'rgba(255,255,255,0.14)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(cx, cy, r - 6, 0, Math.PI * 2); ctx.stroke();
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * (r - 14), cy + Math.sin(a) * (r - 14));
        ctx.lineTo(cx + Math.cos(a) * (r - 6), cy + Math.sin(a) * (r - 6));
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.font = 'bold 24px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('OREO', cx, cy);
    } else {
      const gr = ctx.createRadialGradient(cx - 28, cy - 28, 8, cx, cy, r);
      gr.addColorStop(0, 'rgba(255,255,255,0.45)');
      gr.addColorStop(0.6, 'rgba(255,255,255,0.07)');
      gr.addColorStop(1, 'rgba(0,0,0,0.2)');
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = gr; ctx.fill();

      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + Math.PI * 0.6;
        const sx = cx + Math.cos(a) * r, sy = cy + Math.sin(a) * r;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + Math.cos(a) * 12, sy + Math.sin(a) * 12);
        ctx.strokeStyle = state.chocBase; ctx.lineWidth = 6; ctx.lineCap = 'round'; ctx.stroke();
      }
    }

    drawToppings(ctx, cx, cy, r, r, 'circle');
    if (state.drizzled) drawDrizzle(ctx, cx - r, cx + r, cy - r, cy + r);
  }

  // ── Animations ────────────────────────────────────────────────
  function animateDip() {
    const ctx = getCtx(); if (!ctx) return;
    let progress = 0;

    function frame() {
      progress += 0.055;
      if (progress >= 1) { drawItem(); return; }

      drawItem();
      ctx.save();
      ctx.globalAlpha = Math.max(0, 0.45 - progress * 0.35);
      ctx.fillStyle = state.chocBase;
      ctx.fillRect(0, 0, 232, progress * 232);
      for (let i = 0; i < 8; i++) {
        const dx = i * 30;
        const dy = progress * 232 + Math.sin(progress * 8 + i) * 12;
        ctx.beginPath(); ctx.arc(dx, dy, 10, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      requestAnimationFrame(frame);
    }
    frame();
  }

  function animateDrizzle() {
    const ctx = getCtx(); if (!ctx) return;
    drawItem();
    ctx.save(); ctx.globalAlpha = 0.3; ctx.fillStyle = state.drizzleColor;
    ctx.fillRect(0, 0, 232, 232); ctx.restore();
    setTimeout(() => drawItem(), 160);
  }

  // ── Global event wiring ───────────────────────────────────────
  function init() {
    // Cart toggle
    document.addEventListener('click', e => {
      const cartTrigger = e.target.closest('[data-cart-toggle]');
      if (cartTrigger) {
        qs('#bt-cart-sidebar')?.classList.toggle('is-open');
        qs('#bt-cart-overlay')?.classList.toggle('is-open');
      }

      const cartClose = e.target.closest('[data-cart-close]');
      if (cartClose) {
        qs('#bt-cart-sidebar')?.classList.remove('is-open');
        qs('#bt-cart-overlay')?.classList.remove('is-open');
      }

      const builderTrigger = e.target.closest('[data-open-builder]');
      if (builderTrigger) {
        const item = builderTrigger.dataset.openBuilder;
        if (item) openBuilder(item);
      }

      const builderClose = e.target.closest('[data-builder-close]');
      if (builderClose) closeBuilder();
    });

    // Close overlay on backdrop click
    qs('#bt-builder-overlay')?.addEventListener('click', function(e) {
      if (e.target === this) closeBuilder();
    });
    qs('#bt-cart-overlay')?.addEventListener('click', closeCart);

    function closeCart() {
      qs('#bt-cart-sidebar')?.classList.remove('is-open');
      qs('#bt-cart-overlay')?.classList.remove('is-open');
    }

    // Scroll-triggered animations
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'bt-fade-up 0.55s ease both';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    qsa('.craft-card, .product-card, .drink-card, .testimonial-card, .step-card').forEach(el => {
      el.style.opacity = '0';
      observer.observe(el);
    });

    // Paralax on hero
    window.addEventListener('scroll', () => {
      const hero = qs('.hero-section');
      if (hero) hero.style.backgroundPositionY = window.pageYOffset * 0.4 + 'px';
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
